import { Link, useNavigate } from "react-router";
import { ArrowRight, Loader2, Lock, Mail, ShieldCheck } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";

import { AuthLayout } from "@/components/common/auth-layout";
import { IconInput, PasswordToggle } from "@/components/common/icon-input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { firebaseErrorMessage } from "@/lib/firebase";
import { usePageMeta } from "@/lib/page-meta";

export function SignupRoute() {
  usePageMeta({
    title: "Create account — HealNova Doctor Portal",
    description:
      "Create your HealNova clinician login, then complete your practice registration for review.",
  });
  return <SignupPage />;
}

function SignupPage() {
  const { signUp, user, loading, configured } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/register", { replace: true });
  }, [loading, user, navigate]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError("Choose a password with at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }

    setSubmitting(true);
    try {
      await signUp(email.trim(), password);
      toast.success("Account created. Let's complete your registration.");
      navigate("/register", { replace: true });
    } catch (err) {
      setError(firebaseErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      eyebrow="Join HealNova"
      title="Create your account"
      subtitle="First create your login. Next you'll add your practice details for verification."
      panelTitle="Built for clinicians"
      panelBody="Set up your login, complete your practice profile, and our verification team takes it from there."
      footer={
        <>
          Already registered?{" "}
          <Link to="/login" className="story-link font-semibold text-primary">
            Login instead
          </Link>
        </>
      }
    >
      {!loading && !configured && (
        <Alert variant="destructive" className="mb-7">
          <AlertDescription>
            Authentication isn't configured yet. Add your Firebase web keys to enable sign-up.
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={onSubmit} className="space-y-6" noValidate>
        <div className="space-y-2.5">
          <Label htmlFor="email">Email address</Label>
          <IconInput
            icon={Mail}
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="doctor@clinic.com"
          />
        </div>

        <div className="space-y-2.5">
          <Label htmlFor="password">Password</Label>
          <IconInput
            icon={Lock}
            id="password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            trailing={
              <PasswordToggle visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />
            }
          />
        </div>

        <div className="space-y-2.5">
          <Label htmlFor="confirm">Confirm password</Label>
          <IconInput
            icon={ShieldCheck}
            id="confirm"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Re-enter your password"
          />
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          className="group h-14 w-full rounded-xl text-base"
          disabled={submitting || loading || !configured}
        >
          {submitting && <Loader2 className="size-4 animate-spin" />}
          Create your account
          <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Button>
      </form>
    </AuthLayout>
  );
}
